import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { DiscardConfirm } from './DiscardConfirm';

interface ModalClose {
  /** User-initiated close (Cancel / X / Esc): asks first when something was typed. */
  requestClose: () => void;
  /** Close from code (after a save): never asks. */
  close: () => void;
}

const ModalCloseContext = createContext<ModalClose | null>(null);

export function useModalClose(): ModalClose {
  const ctx = useContext(ModalCloseContext);
  if (!ctx) throw new Error('useModalClose must be used inside <Modal>');
  return ctx;
}

// Open pop-ups by open-order number. Esc is handled by the highest one only.
const openStack: number[] = [];
let nextOrder = 1;
const SHAKE_MS = 300;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Overrides the automatic "something was typed" detection. */
  dirty?: boolean;
  className?: string;
  /** 'right' = full-height panel on the right edge (drawers). Same close rules. */
  side?: 'center' | 'right';
  /** Accessible name when there is no visible title. */
  ariaLabel?: string;
  children: ReactNode;
}

export function Modal({ open, onClose, title, dirty, className, side = 'center', ariaLabel, children }: ModalProps) {
  // Render order = parent before child, so an inner pop-up always gets the higher number.
  const order = useRef(0);
  if (order.current === 0) order.current = nextOrder++;
  const [typed, setTyped] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [shaking, setShaking] = useState(false);
  const shakeTimer = useRef<ReturnType<typeof setTimeout>>();
  const isDirty = dirty ?? typed;

  const close = useCallback(() => {
    setConfirming(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (isDirty) setConfirming(true);
    else close();
  }, [isDirty, close]);

  useEffect(() => {
    if (!open) {
      setTyped(false);
      setConfirming(false);
    }
  }, [open]);

  const handlerRef = useRef<() => void>(() => {});
  handlerRef.current = () => (confirming ? setConfirming(false) : requestClose());

  useEffect(() => {
    if (!open) return;
    const id = order.current;
    openStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || Math.max(...openStack) !== id) return;
      e.preventDefault();
      handlerRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = openStack.indexOf(id);
      if (i >= 0) openStack.splice(i, 1);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(shakeTimer.current), []);

  const ctx = useMemo(() => ({ requestClose, close }), [requestClose, close]);
  if (!open) return null;

  const markTyped = (e: { currentTarget: Element; target: EventTarget }) => {
    // Typing inside a pop-up opened from this one must not mark this one.
    if (e.currentTarget.contains(e.target as Node)) setTyped(true);
  };
  const shake = () => {
    setShaking(true);
    clearTimeout(shakeTimer.current);
    shakeTimer.current = setTimeout(() => setShaking(false), SHAKE_MS);
  };

  return createPortal(
    <ModalCloseContext.Provider value={ctx}>
      <div
        className={`fixed inset-0 z-50 flex bg-black/50 ${side === 'right' ? 'justify-end' : 'items-center justify-center p-4'}`}
        data-testid="modal-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget) shake();
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          className={`relative w-full max-w-lg overflow-y-auto border ${side === 'right' ? 'h-full' : 'max-h-[90vh] rounded-xl'} border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl ${shaking ? 'modal-shake ' : ''}${className ?? ''}`}
          onInputCapture={markTyped}
          onChangeCapture={markTyped}
        >
          {title != null && (
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-5 py-3">
              <h2 className="text-base font-semibold text-[var(--text-primary)]">{title}</h2>
              <button type="button" aria-label="Close" className="btn btn-ghost" onClick={requestClose}>
                <X size={16} />
              </button>
            </div>
          )}
          {children}
        </div>
        {/* Outside the scrolling box, so it is always on screen however tall the pop-up is. */}
        {confirming && <DiscardConfirm onKeep={() => setConfirming(false)} onDiscard={close} />}
      </div>
    </ModalCloseContext.Provider>,
    document.body,
  );
}
